import { ProtectedPagesLayout } from '@/components';
import { ProtectedRoute, PublicRoute } from '@/components/auth';
import { LoadingState } from '@/components/common';
import { routeConfig } from '@/config/appRoutes';
import { RouteConfig } from '@/types/routes';
import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

/**
 * Creates a protected route element with layout and permission checking
 * Follows DRY principle by reusing common patterns
 */
const createProtectedRoute = (config: RouteConfig) => {
  const { component: Component, permission } = config;

  return (
    <ProtectedRoute requiredPermission={permission}>
      <ProtectedPagesLayout>
        <Component />
      </ProtectedPagesLayout>
    </ProtectedRoute>
  );
};

/**
 * Creates a public route element with authentication redirect
 */
const createPublicRoute = (config: RouteConfig) => {
  const { component: Component } = config;

  return (
    <PublicRoute>
      <Component />
    </PublicRoute>
  );
};

/**
 * AppRoutes component - Declarative route configuration
 * Separated from App component following single responsibility principle
 * Wrapped with Suspense for lazy-loaded route components
 */
export const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingState minHeight='100vh' />}>
      <Routes>
        {/* Public routes */}
        {routeConfig.publicRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={createPublicRoute(route)}
          />
        ))}

        {/* Protected routes */}
        {routeConfig.protectedRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={createProtectedRoute(route)}
          />
        ))}

        {/* Redirect routes */}
        {routeConfig.redirectRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={<Navigate to={route.redirectTo!} replace />}
          />
        ))}
      </Routes>
    </Suspense>
  );
};
